import { useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { IAddEditParentAdminFormData } from 'src/components/enterprise/Parents/Admin/types';
import { IErrorResponse } from 'src/utilities/shared-types';
import {
  SdkErrorResponse,
  pushErrorFromAPI,
  pushSuccess,
} from 'src/utilities/notifications';
import { getSsoUniqueIdentifier } from 'src/components/enterprise/Parents/Admin/functions/getSsoUniqueIdentifier';
import { useCreateEmployee } from 'src/components/enterprise/features/Employee/CreatePage/hooks/useCreateEmployee';
import { ROLES } from 'src/types/Employee';

export type UseCreateParentAdminProps = {
  corpId: string;
};
export type UseCreateParentAdminReturn = {
  createParentAdmin: (
    parentAdminFormData: IAddEditParentAdminFormData
  ) => Promise<void>;
  isLoading: boolean;
};

export const useCreateParentAdmin = ({
  corpId,
}: UseCreateParentAdminProps): UseCreateParentAdminReturn => {
  const { push } = useHistory();
  const formDataRef = useRef<IAddEditParentAdminFormData | null>(null);

  const handleSuccess = async (): Promise<void> => {
    const formData = formDataRef.current;
    pushSuccess(`${formData?.firstName} has been added to your parent admins.`);
    const parentOverviewLink = `/corporate/enterprise/parents/${corpId}/admin`;
    push(parentOverviewLink);
  };

  const handleError = async (
    error: IErrorResponse | SdkErrorResponse
  ): Promise<void> => {
    const formData = formDataRef.current;
    pushErrorFromAPI(error, {
      fallbackMessage: `${formData?.firstName} ${formData?.lastName} was not created. Try again.`,
    });
  };

  const { createEmployee: create, isLoading } = useCreateEmployee({
    corpId,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const createParentAdmin = async (
    parentAdminFormData: IAddEditParentAdminFormData
  ): Promise<void> => {
    // eslint-disable-next-line better-mutation/no-mutation
    formDataRef.current = parentAdminFormData;

    await create({
      firstName: parentAdminFormData.firstName,
      lastName: parentAdminFormData.lastName,
      email: parentAdminFormData.email,
      phoneNumber: parentAdminFormData.phoneNumber,
      internalId: parentAdminFormData.internalId,
      role: ROLES.CORP_PARENT_ADMIN,
      corpDinerSettings: {
        ssoUniqueIdentifier: getSsoUniqueIdentifier(parentAdminFormData),
      },
    });
  };

  return {
    createParentAdmin,
    isLoading,
  };
};
